// frontend/src/context/Branding.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { getJSON } from "../services/http";

/**
 * Derive current tenant slug from URL path.
 * Caches to localStorage so non-tenant routes can still resolve a known slug.
 */
function getTenantSlug() {
  try {
    const m = (window.location.pathname || "").match(/^\/t\/([^/]+)/i);
    if (m && m[1]) {
      localStorage.setItem("tenantSlug", m[1]);
      return m[1];
    }
    return localStorage.getItem("tenantSlug") || "";
  } catch {
    return "";
  }
}

/**
 * Prefix a relative API path with /t/:slug unless the path is already tenant- or owner-scoped
 * or an absolute URL.
 */
function withTenantPrefix(path) {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;         // absolute URL, leave as is
  if (/^\/t\//.test(path)) return path;                // already tenant-prefixed
  if (/^\/api\/owner(\/|$)/.test(path)) return path;   // owner endpoints are global
  const slug = getTenantSlug();
  return slug ? `/t/${slug}${path.startsWith("/") ? "" : "/"}${path}` : path;
}

const BrandingCtx = createContext(null);
export const useBranding = () => useContext(BrandingCtx);

export default function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null); // { slug, name, dept, theme, features }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // Always fetch the public tenant metadata with tenant prefix
        const res = await getJSON(withTenantPrefix("/api/tenant/public"));
        if (alive) setBranding(res || null);
      } catch {
        if (alive) setBranding(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    // re-run if the path (and thus potential /t/:slug) changes
    // eslint-disable-next-line
  }, [window.location.pathname]);

  return (
    <BrandingCtx.Provider value={{ branding, loading }}>
      {children}
    </BrandingCtx.Provider>
  );
}
